"use client";

import React from "react";
import { Instagram } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type SupportedLanguage = "en" | "zh" | "fr" | "es" | "de";
function useWvcLanguage(): SupportedLanguage {
  const [lang, setLang] = React.useState<SupportedLanguage>("en");
  React.useEffect(() => {
    const handleLangChange = () => {
      const htmlLang = document.documentElement.lang?.slice(0, 2).toLowerCase();
      if (htmlLang && ["en", "zh", "fr", "es", "de"].includes(htmlLang)) {
        setLang(htmlLang as SupportedLanguage);
      }
    };
    handleLangChange();
    const observer = new MutationObserver(handleLangChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"]
    });
    return () => observer.disconnect();
  }, []);
  return lang;
}
const translations: Record<SupportedLanguage, {
  styledBy: string;
  you: string;
  followUs: string;
}> = {
  en: {
    styledBy: "Styled by",
    you: "you",
    followUs: "Follow Us on Instagram"
  },
  zh: {
    styledBy: "由...款式",
    you: "你",
    followUs: "在Instagram上关注我们"
  },
  fr: {
    styledBy: "Stylisé par",
    you: "vous",
    followUs: "Suivez-Nous sur Instagram"
  },
  es: {
    styledBy: "Estilizado por",
    you: "tú",
    followUs: "Síguenos en Instagram"
  },
  de: {
    styledBy: "Gestylt von",
    you: "dir",
    followUs: "Folgen Sie uns auf Instagram"
  }
};
interface GalleryItem {
  id: string;
  src: string;
  srcset: string;
  sizes: string;
  alt: string;
  tag: string;
}
const GALLERY_ITEMS: GalleryItem[] = [{
  id: "look-1",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_1.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_1.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_1.png-wvc-sizes",
  alt: "Relaxed tailored linen blazer styled with pleated trousers in sand tone",
  tag: "@claire.journal"
}, {
  id: "look-2",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_2.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_2.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_2.webp-wvc-sizes",
  alt: "Minimalist structured poplin shirt and silk drape skirt",
  tag: "@elena_studio"
}, {
  id: "look-3",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_3.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_3.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_3.png-wvc-sizes",
  alt: "Textural knit cardigan with tortoiseshell buttons and gold accessories",
  tag: "@sophie_edit"
}, {
  id: "look-4",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_4.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_4.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_4.webp-wvc-sizes",
  alt: "Charcoal monochrome wool coat over fine cashmere crewneck",
  tag: "@mara.minimal"
}, {
  id: "look-5",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_5.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_5.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/instagram_look_5.webp-wvc-sizes",
  alt: "Fluid column evening dress in ivory crepe with architectural neckline",
  tag: "@hannah_atelier"
}];
export default function Lookbook() {
  const language = useWvcLanguage();
  return <section id="instagram展示" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="max-w-[1360px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Header Block */}
        <div className="text-center max-w-xl mx-auto mb-10 md:mb-14 space-y-2">
          <p className="font-sans text-xs md:text-sm font-semibold tracking-[0.15em] uppercase text-muted-foreground">
            @yishengfushi
          </p>
          <h2 className="font-serif text-2xl md:text-4xl lg:text-5xl font-normal tracking-tight text-foreground">
            {translations[language].styledBy}{" "}
            <span className="italic font-serif">{translations[language].you}</span>
          </h2>
        </div>

        {/* 5-Column 1:1 Gallery Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 lg:gap-5">
          {GALLERY_ITEMS.map((item, i) => <div key={item.id} data-index={i} className="group relative aspect-square overflow-hidden bg-muted rounded-none">
              <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03] motion-reduce:transform-none" />

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-foreground/35 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-out flex flex-col items-center justify-center p-3 text-center pointer-events-none">
                <div className="w-10 h-10 rounded-full bg-background/90 text-foreground flex items-center justify-center shadow-sm transform translate-y-1 group-hover:translate-y-0 transition-transform duration-200 ease-out motion-reduce:transform-none">
                  <Instagram className="size-5" />
                </div>
                <span className="mt-2 text-xs font-medium text-background tracking-wide drop-shadow-xs">
                  {item.tag}
                </span>
              </div>
            </div>)}
        </div>

        {/* Action Button */}
        <div className="mt-10 md:mt-14 flex justify-center">
          <Button asChild variant="secondary" size="lg" className="rounded-none px-8 py-3 text-sm font-medium tracking-wide bg-secondary text-secondary-foreground hover:bg-secondary/85 transition-colors duration-200">
            <Link to="https://instagram.com" newTab aria-label="Follow yishengfushi on Instagram (opens in a new tab)">
              {translations[language].followUs}
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}